import React, {useContext, useEffect, useState, useCallback } from "react";
import ButtonBar from '../../Elements/ButtonBar'
import BulkActionsBar from '../../Elements/BulkActionsBar';
import SuperuserModal from "./SuperuserModal";
import SuperuserDetailModal from "./SuperuserDetailModal";
import OwnersModal from '../OwnersComponents/OwnersModal';
import { UserContext } from "../../../context/UserContext";
import { getImageUrl } from "../../../lib/helpers";
import { FaEdit } from "react-icons/fa";
import { FaDeleteLeft } from "react-icons/fa6";

function profilePhotoUrl(profile) {
  return getImageUrl(profile?.documents_path || profile?.image_path || null);
}

export default function Superusers() {
  const [loaded, setLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [token] = useContext(UserContext);
  const [superusers, setSuperusers] = useState([]);
  const [filteredSuperusers, setFilteredSuperusers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [id, setId] = useState(null);
  const [activeModal, setActiveModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedSuperuser, setSelectedSuperuser] = useState(null);
  const [ownerModalActive, setOwnerModalActive] = useState(false);
  const [selectedSuperuserIds, setSelectedSuperuserIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const menu_bar = [
    {
      title: 'Super Usuarios',
      buttons: [
        {
          label: 'Crear Superusuario',
          color: 'blue',
          onClick: () => {
            setId(null);
            setActiveModal(true);
          },
        },
        {
          label: 'Crear Owner',
          color: 'green',
          onClick: () => {
            setOwnerModalActive(true);
          },
        },
      ],
    },
  ];


  const getUsers = useCallback(async () => {
    if (!token) {
      setErrorMessage('No hay token de autenticación');
      setLoaded(true);
      return;
    }

    setLoaded(false);
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    };

    try {
      const response = await fetch(`/v1/users/all`, requestOptions);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Error al obtener usuarios' }));
        setErrorMessage(errorData.detail || 'Error al obtener usuarios');
        setSuperusers([]);
        setLoaded(true);
        return;
      }

      const data = await response.json();
      
      // Filtrar solo usuarios con privilegios de superadmin
      const filteredSuperusers = data.filter(user => user.access_privileges === 'superadmin');
      setSuperusers(filteredSuperusers);
      setFilteredSuperusers(filteredSuperusers);
      setErrorMessage("");
      setLoaded(true);
    } catch (error) {
      console.error("❌ Error fetching users:", error);
      setSuperusers([]);
      setErrorMessage(error.message || 'Error al obtener usuarios');
      setLoaded(true);
    }
  }, [token]);

  const handleEdit = (id) => {
    setId(id);
    setActiveModal(true);
  };

  const handleDelete = async (id) => {
    const confirmMessage = '¿Estás seguro de que quieres eliminar este superusuario?';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const requestOptions = {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      };
      const response = await fetch(`/v1/users/${id}`, requestOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Error al eliminar superusuario' }));
        setErrorMessage(errorData.detail || 'Error al eliminar superusuario');
        return;
      }
      
      await getUsers();
      setErrorMessage("");
    } catch (error) {
      console.error("Error deleting superuser:", error);
      setErrorMessage(error.message || 'Error al eliminar superusuario');
    }
  };

  useEffect(() => {
    setSelectedSuperuserIds((prev) =>
      prev.filter((id) => superusers.some((user) => String(user.id) === String(id)))
    );
  }, [superusers]);

  const toggleSelectedSuperuser = (id) => {
    setSelectedSuperuserIds((prev) =>
      prev.some((currentId) => String(currentId) === String(id))
        ? prev.filter((currentId) => String(currentId) !== String(id))
        : [...prev, id]
    );
  };

  const clearSelectedSuperusers = () => setSelectedSuperuserIds([]);

  const selectVisibleSuperusers = () => {
    setSelectedSuperuserIds(filteredSuperusers.map((superuser) => superuser.id));
  };

  const deleteSuperuserById = async (id) => {
    const response = await fetch(`/v1/users/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Error al eliminar superusuario' }));
      throw new Error(errorData.detail || 'Error al eliminar superusuario');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedSuperuserIds.length) return;
    if (!window.confirm(`¿Eliminar ${selectedSuperuserIds.length} superusuarios seleccionados?`)) return;

    setIsBulkDeleting(true);
    const results = await Promise.allSettled(selectedSuperuserIds.map(deleteSuperuserById));
    const failedCount = results.filter((result) => result.status === 'rejected').length;
    await getUsers();
    setIsBulkDeleting(false);
    setSelectedSuperuserIds([]);

    if (failedCount > 0) {
      setErrorMessage(`Se eliminaron ${selectedSuperuserIds.length - failedCount} superusuarios y ${failedCount} fallaron.`);
      return;
    }

    setErrorMessage("");
  };

  const handleModal = () => {
    setActiveModal(!activeModal);
    if (!activeModal) {
      getUsers();
    }
    setId(null);
  };

  useEffect(() => {
    if (token) {
      getUsers();
    }
  }, [token, getUsers]);

  // Búsqueda en tiempo real
  useEffect(() => {
    if (!searchInput || searchInput.trim() === "") {
      setFilteredSuperusers(superusers);
      return;
    }

    const searchTerm = searchInput.toLowerCase();
    const filtered = superusers.filter(user => {
      if (!user) return false;
      
      const firstName = user.profile?.first_name || '';
      const lastName = user.profile?.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const email = user.email || '';
      const id = user.id?.toString() || '';
      
      return (
        fullName.toLowerCase().includes(searchTerm) ||
        firstName.toLowerCase().includes(searchTerm) ||
        lastName.toLowerCase().includes(searchTerm) ||
        email.toLowerCase().includes(searchTerm) ||
        id.toLowerCase().includes(searchTerm)
      );
    });
    
    setFilteredSuperusers(filtered);
  }, [searchInput, superusers]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  };

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-w-full min-w-0">
      {/* Desktop: barra normal */}
      <div className="hidden lg:block">
        {menu_bar.map((menuItem, index) => (
          <ButtonBar
            key={index}
            title={menuItem.title}
            buttons={menuItem.buttons}
          />
        ))}
      </div>

      {/* Móvil: igual que Socios (botones alargados) */}
      <div className="block lg:hidden bg-white p-4 rounded-lg shadow border border-gray-200 mb-3 space-y-3">
        <h2 className="text-xl font-semibold text-gray-800">
          Super Usuarios
        </h2>
        <div className="space-y-2">
          <button
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
            onClick={() => {
              setId(null);
              setActiveModal(true);
            }}
          >
            Crear Superusuario
          </button>
          <button
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
            onClick={() => setOwnerModalActive(true)}
          >
            Crear Owner
          </button>
        </div>
      </div>

        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{errorMessage}</span>
            <button
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
              onClick={() => setErrorMessage("")}
            >
              <span className="text-red-700">×</span>
            </button>
          </div>
        )}

        {!loaded && (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Cargando superusuarios...</p>
            </div>
          </div>
        )}

        <SuperuserModal
          active={activeModal}
          handleModal={handleModal}
          token={token}
          id={id}
          setErrorMessage={setErrorMessage}
          onSuccess={(updatedSuperuser) => {
            if (updatedSuperuser?.id) {
              setSuperusers((prev) => {
                const existing = prev.some((user) => String(user.id) === String(updatedSuperuser.id));
                const next = existing
                  ? prev.map((user) => (String(user.id) === String(updatedSuperuser.id) ? updatedSuperuser : user))
                  : [updatedSuperuser, ...prev];
                return next.filter((user) => user.access_privileges === 'superadmin');
              });
              setFilteredSuperusers((prev) => {
                const existing = prev.some((user) => String(user.id) === String(updatedSuperuser.id));
                const next = existing
                  ? prev.map((user) => (String(user.id) === String(updatedSuperuser.id) ? updatedSuperuser : user))
                  : [updatedSuperuser, ...prev];
                return next.filter((user) => user.access_privileges === 'superadmin');
              });
              setSelectedSuperuser((prev) => (String(prev?.id) === String(updatedSuperuser.id) ? updatedSuperuser : prev));
              return;
            }
            getUsers();
          }}
        />

        <OwnersModal
          active={ownerModalActive}
          handleModal={() => {
            setOwnerModalActive(false);
            getUsers(); // Recargar para ver cambios
          }}
          token={token}
          id={null}
          setErrorMessage={setErrorMessage}
          onSuccess={() => {
            getUsers();
          }}
        />

        <SuperuserDetailModal
          active={detailModal}
          handleModal={() => {
            setDetailModal(false);
            setSelectedSuperuser(null);
          }}
          superuser={selectedSuperuser}
          token={token}
          onEdit={() => {
            if (selectedSuperuser) {
              handleEdit(selectedSuperuser.id);
            }
          }}
          onDelete={() => {
            if (selectedSuperuser) {
              handleDelete(selectedSuperuser.id);
            }
          }}
        />

      {/* Buscador en tiempo real (igual que Socios) */}
      <div className='bg-white justify-left shadow-2xl'>
        <div className="bg-white p-3 sm:p-4 rounded-lg flex items-center border border-gray-200 justify-between rounded">
          <input
            className="text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-10 pl-11 pr-4 rounded"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por: Nombre, Email, ID..."
            value={searchInput}
          />
        </div>
      </div>

      <BulkActionsBar
        selectedCount={selectedSuperuserIds.length}
        onClear={clearSelectedSuperusers}
        onSelectAll={selectVisibleSuperusers}
        onDelete={handleBulkDelete}
        deleteLabel="Eliminar superusuarios"
        disabled={isBulkDeleting}
      />

      {/* Vista tarjetas móvil/tablet (igual que Socios) */}
      <div className="block lg:hidden space-y-3">
        {!loaded ? (
          <div className="bg-white rounded-lg shadow border p-8 text-center text-gray-500">
            Cargando superusuarios...
          </div>
        ) : filteredSuperusers.length === 0 ? (
          <div className="bg-white rounded-lg shadow border p-8 text-center text-gray-500">
            {searchInput === "" ? "No hay superusuarios disponibles" : "No se encontraron resultados"}
          </div>
        ) : (
          filteredSuperusers.map((superuser) => {
            const imageUrl = profilePhotoUrl(superuser.profile);
            const displayName = superuser.profile?.first_name && superuser.profile?.last_name
              ? `${superuser.profile.first_name} ${superuser.profile.last_name}`
              : superuser.email;
            return (
              <div
                key={superuser.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-purple-300 transition-all duration-200"
              >
                <div className="mb-3 flex justify-end">
                  <input
                    type="checkbox"
                    checked={selectedSuperuserIds.some((id) => String(id) === String(superuser.id))}
                    onChange={() => toggleSelectedSuperuser(superuser.id)}
                    className="h-4 w-4 accent-purple-600"
                  />
                </div>
                <div
                  className="flex items-center gap-3 mb-4 cursor-pointer group"
                  onClick={() => {
                    setSelectedSuperuser(superuser);
                    setDetailModal(true);
                  }}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={displayName}
                      className="w-32 h-32 rounded-xl object-cover border-2 border-gray-200 group-hover:border-purple-400 transition-colors flex-shrink-0"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-32 h-32 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold text-2xl text-3xl shadow-sm flex-shrink-0 ${imageUrl ? 'hidden' : 'flex'}`}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 truncate text-base group-hover:text-purple-600 transition-colors">
                      {displayName}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">{superuser.email}</div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 mb-0.5">ID</span>
                      <span className="font-semibold text-gray-900">#{superuser.id?.toString().slice(0, 8) || '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 mb-0.5">Creado</span>
                      <span className="font-semibold text-gray-900">{formatDate(superuser.created_at) || '-'}</span>
                    </div>
                    <div className="flex flex-col col-span-2">
                      <span className="text-xs text-gray-500 mb-0.5">Privilegios</span>
                      <span className="px-2 py-1 text-xs font-semibold rounded-full w-fit bg-purple-100 text-purple-800">
                        Superadmin
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(superuser.id); }}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 bg-lime-600 hover:bg-lime-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                    >
                      <FaEdit className="text-sm" />
                      <span>Editar</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(superuser.id); }}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                    >
                      <FaDeleteLeft className="text-sm" />
                      <span>Eliminar</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Vista tabla para desktop (igual que Socios) */}
      <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow border">
        <table className="min-w-full text-sm text-gray-700">
              <thead className="bg-gray-50 text-xs uppercase text-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left w-12">
                    <span className="sr-only">Seleccionar</span>
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    Usuario
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    Privilegios
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    Fecha Creación
                  </th>
                  <th scope="col" className="px-4 py-3 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loaded ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      Cargando superusuarios...
                    </td>
                  </tr>
                ) : filteredSuperusers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      {searchInput === "" ? "No hay superusuarios disponibles" : "No se encontraron resultados"}
                    </td>
                  </tr>
                ) : (
                  filteredSuperusers.map((superuser) => {
                    const imageUrl = profilePhotoUrl(superuser.profile);

                    const displayName = superuser.profile?.first_name && superuser.profile?.last_name
                      ? `${superuser.profile.first_name} ${superuser.profile.last_name}`
                      : superuser.profile?.name && superuser.profile?.last_name
                      ? `${superuser.profile.name} ${superuser.profile.last_name}`
                      : 'Sin nombre';

                    const formatDate = (dateString) => {
                      if (!dateString) return '-';
                      try {
                        const date = new Date(dateString);
                        if (isNaN(date.getTime())) return '-';
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                      } catch {
                        return '-';
                      }
                    };

                    return (
                      <tr key={superuser.id} className="even:bg-white odd:bg-gray-50 hover:bg-gray-100 transition-colors">
                        <td className="px-4 py-4 align-middle">
                          <input
                            type="checkbox"
                            checked={selectedSuperuserIds.some((id) => String(id) === String(superuser.id))}
                            onChange={() => toggleSelectedSuperuser(superuser.id)}
                            className="h-4 w-4 accent-purple-600"
                          />
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <div
                            className="flex cursor-pointer items-center gap-3 transition-opacity hover:opacity-80"
                            onClick={() => {
                              setSelectedSuperuser(superuser);
                              setDetailModal(true);
                            }}
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={displayName !== 'Sin nombre' ? displayName : superuser.email}
                                className="h-12 w-12 flex-shrink-0 rounded-full border border-gray-300 object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gray-300 font-semibold text-gray-600 ${imageUrl ? 'hidden' : 'flex'}`}
                            >
                              {(displayName !== 'Sin nombre' ? displayName : superuser.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-gray-900">{displayName !== 'Sin nombre' ? displayName : 'Usuario'}</div>
                              <div className="truncate text-xs text-gray-500">ID: {superuser.id?.toString().slice(0, 8) || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 align-middle">{superuser.email}</td>
                        <td className="px-4 py-4 align-middle">
                          <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800">
                            Superadmin
                          </span>
                        </td>
                        <td className="px-4 py-4 align-middle">
                          {formatDate(superuser.created_at) || '-'}
                        </td>
                        <td className="space-x-1 px-4 py-4 text-right align-middle">
                          <button 
                            onClick={() => handleEdit(superuser.id)}
                            className="p-2 bg-lime-600 hover:bg-lime-800 text-white rounded"
                            title="Editar superusuario"
                          >
                            <FaEdit/>
                          </button>
                          <button 
                            onClick={() => handleDelete(superuser.id)} 
                            className="p-2 bg-red-700 hover:bg-red-800 text-white rounded"
                            title="Eliminar superusuario"
                          >
                            <FaDeleteLeft/>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
  );
}
